<!--
  @component

  @slot required `default` Main content of the banner
  @slot optional `actions` Link elements [oc-link](/?path=/docs/elements-link--overview) which provide interactions to the user
-->
<svelte:options
  customElement={{
    tag: "oc-banner-v1",
    shadow: "none",
    /*                                                         */
    /*         */
    extend: (CustomElementConstructor) =>
      class extends CustomElementConstructor {
        constructor() {
          super();
          this.attachShadow({ mode: "open", delegatesFocus: true });
        }
      },
    props: {
      headline: { type: "String" },
      hideCloseButton: { type: "Boolean", attribute: "hide-close-button" },
      hideIcon: { type: "Boolean", attribute: "hide-icon" },
      iconType: { type: "String", attribute: "icon-type" },
      size: { type: "String", reflect: true },
      variant: { type: "String" },
      hidden: { type: "Boolean", reflect: true },
    },
  }}
/>

<script lang="ts">
  import { createEventDispatcher } from "svelte";
  import type { OcIconV1Props } from "../../icon";
  import { iconTypeByBannerType } from "./consts";
  import { usePropertyChange } from "../../../common/utils/usePropertyChange";

  usePropertyChange();

  /**
 *
 *
 */
  export let headline: string | undefined;
  /**
 *
 *
 */
  export let hideCloseButton = false;
  /**
 *
 *
 */
  export let hideIcon = false;
  /*                                                          */
  export let iconType: OcIconV1Props["type"] | undefined;
  /**
 *
 *
 */
  export let size: "100" | "200" | "300" = "100";
  /**
 *
 *
 */
  export let variant: keyof typeof iconTypeByBannerType = "info";
  /**
 *
 *
 */
  export let hidden = false;

  $: computedIconType = iconType || iconTypeByBannerType[variant];

  const dispatch = createEventDispatcher<{
    /**
 *
 */
    "oc-close": undefined;
  }>();

  export function handleClose({ type, code }: KeyboardEvent & MouseEvent) {
    if (
      type === "click" ||
      (type === "keyup" && code === "Space") ||
      (type === "keyup" && code === "Enter")
    ) {
      dispatch("oc-close");
      hidden = true;
    }
  }
</script>

<div
  class="banner {`banner--type-${variant}`} {`banner--size-${size}`}"
  class:banner--no-icon={hideIcon}
  class:banner--hide-close-button={hideCloseButton}
  data-testid="banner"
>
  {#if !hideIcon}
    <oc-icon-v1 class="banner__icon" type={computedIconType} data-testid="icon" />
  {/if}

  {#if headline && size !== "100"}
    <h1 class="banner__headline">{headline}</h1>
  {/if}

  <div class="banner__body">
    <!-- default slot -->
    <slot />
  </div>

  {#if $$slots.actions}
    <div class="banner__actions">
      <slot name="actions" />
    </div>
  {/if}

  {#if !hideCloseButton}
    <oc-icon-button-v1
      class="banner__close-button"
      data-testid="button"
      icon-type="close"
      size={"50"}
      transparent
      role="button"
      tabindex="0"
      on:click={handleClose}
      on:keyup={handleClose}
      aria-label={"schließen"}
    ></oc-icon-button-v1>
  {/if}
</div>

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";

  .banner {
    display: block;
    position: relative;
    padding: tokens.$oc-component-banner-100-spacing;
    padding-left: 52px;
    border-radius: tokens.$oc-component-banner-border-radius;
    min-width: 240px;
    min-height: 24px;
    background-color: tokens.$oc-component-banner-info-background-color;

    &__icon {
      position: absolute;
      top: 16px;
      left: 16px;
    }

    &__headline {
      font: tokens.$oc-component-banner-info-headline-color;
      margin-block-start: 0;
      margin-block-end: 0;
    }

    &__body {
      margin-right: 36px;
      font: tokens.$oc-component-banner-copy-font;
      color: tokens.$oc-component-banner-copy-color;
    }

    &__actions {
      display: flex;
      gap: tokens.$oc-component-banner-100-gap-x;
      margin-top: tokens.$oc-component-banner-100-gap-y;
    }

    &__close-button {
      position: absolute;
      top: 12px;
      right: 12px;
      padding: 0;
      background: none;
      border: none;
    }

    &--type-error {
      background-color: tokens.$oc-component-banner-error-background-color;

      .banner__headline {
        color: tokens.$oc-component-banner-error-headline-color;
      }

      .banner__icon {
        fill: tokens.$oc-component-banner-error-icon-color;
      }
    }

    &--type-success {
      background-color: tokens.$oc-component-banner-success-background-color;

      .banner__headline {
        color: tokens.$oc-component-banner-success-headline-color;
      }

      .banner__icon {
        fill: tokens.$oc-component-banner-success-icon-color;
      }
    }

    &--type-warning {
      background-color: tokens.$oc-component-banner-warning-background-color;

      .banner__headline {
        color: tokens.$oc-component-banner-warning-headline-color;
      }

      .banner__icon {
        fill: tokens.$oc-component-banner-warning-icon-color;
      }
    }

    &--type-hint {
      background-color: tokens.$oc-component-banner-hint-background-color;

      .banner__headline {
        color: tokens.$oc-component-banner-hint-headline-color;
      }

      .banner__icon {
        fill: tokens.$oc-component-banner-hint-icon-color;
      }
    }

    &--type-info {
      background-color: tokens.$oc-component-banner-info-background-color;

      .banner__headline {
        color: tokens.$oc-component-banner-info-headline-color;
      }

      .banner__icon {
        fill: tokens.$oc-component-banner-info-icon-color;
      }
    }

    &--size-100 {
      .banner__icon {
        width: tokens.$oc-component-banner-100-icon-size;
        height: tokens.$oc-component-banner-100-icon-size;
      }
    }

    &--size-200 {
      .banner__headline {
        font: tokens.$oc-component-banner-200-headline-font;
        padding-bottom: tokens.$oc-component-banner-200-gap-y;
        margin-right: 36px;
      }

      .banner__actions {
        justify-content: flex-end;
      }

      .banner__body {
        margin-right: 0;
        padding-bottom: tokens.$oc-component-banner-200-text-spacing-bottom;
      }

      .banner__actions {
        gap: tokens.$oc-component-banner-200-gap-x;
      }

      .banner__icon {
        width: tokens.$oc-component-banner-200-icon-size;
        height: tokens.$oc-component-banner-200-icon-size;
      }
    }

    &--size-300 {
      padding: tokens.$oc-component-banner-300-spacing;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: tokens.$oc-component-banner-300-gap-y;

      .banner__icon {
        position: static;
        width: tokens.$oc-component-banner-300-icon-size;
        height: tokens.$oc-component-banner-300-icon-size;
      }

      .banner__headline {
        font: tokens.$oc-component-banner-300-headline-font;
        text-align: center;
        padding-bottom: 0;
      }

      .banner__actions {
        width: 100%;
        justify-content: flex-end;
      }

      .banner__body {
        margin-right: 0;
        margin-bottom: tokens.$oc-component-banner-300-text-spacing-bottom;
      }

      .banner__actions {
        gap: tokens.$oc-component-banner-300-gap-x;
        margin-top: 0;
      }
    }

    &--no-icon {
      padding-left: 16px;
    }

    &--hide-close-button {
      padding-right: 16px;

      .banner__body {
        margin-right: 0px;
      }

      .banner__headline {
        margin-right: 0px;
      }
    }

    &--no-icon.banner--hide-close-button {
      padding-left: 16px;
      padding-right: 16px;
    }
  }
</style>
